import React, { useState, useEffect } from 'react';
import {
  LayoutDashboard,
  Briefcase,
  Users,
  Award,
  FileText,
  Zap,
  Clock,
  BarChart3,
  Settings,
  Sparkles,
  Search,
  Plus,
  Compass,
  ArrowRight,
  Edit3
} from 'lucide-react';
import { UserProfile } from '../../lib/firebase';
import {
  PracticeClient,
  PracticeCase,
  PracticeConsultation,
  PracticeReport,
  TimingRadarEvent,
  FollowUpItem,
  PracticeAnalyticsSummary,
  CaseStage
} from '../../types/practiceManagement';
import { Language, CompleteKundali } from '../../types/kundali';
import { formatDOB } from '../../utils/dateUtils';
import { APP_VERSION } from '../../types';
import { isSuperAdminUser } from '../../utils/rbac';
import {
  getPracticeClients,
  getPracticeCases,
  getPracticeConsultations,
  getPracticeReports,
  getTimingRadarEvents,
  getFollowUpItems,
  getPracticeAnalytics,
  updateCaseStage,
  savePracticeCase,
  savePracticeReport,
  updateConsultationStatus,
  completeFollowUpItem
} from '../../lib/practiceManagementService';

import { PracticeOverview } from './PracticeOverview';
import { CasePipeline } from './CasePipeline';
import { CaseWorkspace } from './CaseWorkspace';
import { ClientManagementHub } from './ClientManagementHub';
import { ConsultationManager } from './ConsultationManager';
import { ReportingCentre } from './ReportingCentre';
import { TimingRadar } from './TimingRadar';
import { FollowUpCentre } from './FollowUpCentre';
import { PracticeAnalytics } from './PracticeAnalytics';
import { PracticeSettingsModal } from './PracticeSettingsModal';
import { NewClientModal } from './NewClientModal';
import { NewCaseModal } from './NewCaseModal';
import { ConsultantCRMView } from '../ConsultantCRMView';

interface ManagementWorkspaceProps {
  kundali?: CompleteKundali;
  lang: Language;
  currentUser?: UserProfile | null;
  onOpenKundaliForClient?: (client: PracticeClient) => void;
  onOpenProAnalysis?: (caseItem: PracticeCase) => void;
  onOpenAcharyaConsultation?: (caseItem: PracticeCase) => void;
  onEditKundali?: () => void;
}

export const ManagementWorkspace: React.FC<ManagementWorkspaceProps> = ({
  kundali,
  lang,
  currentUser,
  onOpenKundaliForClient,
  onOpenProAnalysis,
  onOpenAcharyaConsultation,
  onEditKundali
}) => {
  const isHi = lang === 'hi';

  // Navigation tab state
  const [activeNavTab, setActiveNavTab] = useState<string>('overview');

  // Loaded State
  const [clients, setClients] = useState<PracticeClient[]>([]);
  const [cases, setCases] = useState<PracticeCase[]>([]);
  const [consultations, setConsultations] = useState<PracticeConsultation[]>([]);
  const [reports, setReports] = useState<PracticeReport[]>([]);
  const [timingEvents, setTimingEvents] = useState<TimingRadarEvent[]>([]);
  const [followUps, setFollowUps] = useState<FollowUpItem[]>([]);
  const [analytics, setAnalytics] = useState<PracticeAnalyticsSummary>(getPracticeAnalytics());

  // Deep view selections
  const [activeCaseWorkspace, setActiveCaseWorkspace] = useState<PracticeCase | null>(null);

  // Modals state
  const [showSettingsModal, setShowSettingsModal] = useState<boolean>(false);
  const [showNewClientModal, setShowNewClientModal] = useState<boolean>(false);
  const [showNewCaseModal, setShowNewCaseModal] = useState<boolean>(false);

  const refreshData = () => {
    setClients(getPracticeClients());
    setCases(getPracticeCases());
    setConsultations(getPracticeConsultations());
    setReports(getPracticeReports());
    setTimingEvents(getTimingRadarEvents());
    setFollowUps(getFollowUpItems());
    setAnalytics(getPracticeAnalytics());
  };

  useEffect(() => {
    refreshData();
  }, []);

  const handleMoveStage = (caseId: string, newStage: CaseStage) => {
    updateCaseStage(caseId, newStage);
    refreshData();
  };

  const handleUpdateCase = (updated: PracticeCase) => {
    savePracticeCase(updated);
    setActiveCaseWorkspace(updated);
    refreshData();
  };

  const handleSaveReport = (report: PracticeReport) => {
    savePracticeReport(report);
    refreshData();
  };

  const handleApproveReport = (reportId: string) => {
    const rep = reports.find((r) => r.id === reportId);
    if (rep) {
      savePracticeReport({ ...rep, status: 'APPROVED' });
      refreshData();
    }
  };

  const handleDeliverReport = (reportId: string) => {
    const rep = reports.find((r) => r.id === reportId);
    if (rep) {
      savePracticeReport({ ...rep, status: 'DELIVERED' });
      refreshData();
    }
  };

  const navItems = [
    { id: 'overview', labelEn: 'Practice Overview', labelHi: 'अभ्यास अवलोकन', icon: LayoutDashboard },
    { id: 'cases', labelEn: 'Cases & Readings', labelHi: 'केस एवं प्रकरण', icon: Briefcase, badge: cases.filter(c => c.stage !== 'DELIVERED').length },
    { id: 'clients', labelEn: 'Client CRM', labelHi: 'क्लाइंट डायरेक्टरी', icon: Users, badge: clients.length },
    { id: 'consultations', labelEn: 'Consultations', labelHi: 'परामर्श सत्र', icon: Award },
    { id: 'reports', labelEn: 'Reporting Centre', labelHi: 'रिपोर्टिंग केंद्र', icon: FileText },
    { id: 'timing', labelEn: 'Timing Radar', labelHi: 'गोचर रडार (30d)', icon: Zap, badge: timingEvents.filter(e => e.daysUntilEvent <= 30).length },
    { id: 'followups', labelEn: 'Follow-Ups', labelHi: 'फॉलो-अप', icon: Clock, badge: followUps.filter(f => f.status === 'PENDING' || f.status === 'OVERDUE').length },
    { id: 'analytics', labelEn: 'Practice Analytics', labelHi: 'परिचालन विश्लेषण', icon: BarChart3 },
    { id: 'practice_crm', labelEn: 'Practice CRM & Invoicing', labelHi: 'प्रैक्टिस CRM एवं बिलिंग', icon: Users }
  ];

  return (
    <div className="space-y-4 max-w-7xl mx-auto pb-12">
      {/* 1. Management Workspace Top Header & Nav Strip */}
      <div className="bg-astro-bg rounded-2xl border border-astro-border shadow-xs overflow-hidden">
        {/* Practice Brand Strip */}
        <div className="p-4 md:px-6 bg-astro-card border-b border-astro-border flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-astro-primary text-white shadow-xs">
              <Sparkles className="w-5 h-5 text-astro-accent" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-base font-bold font-serif text-astro-text">
                  {isHi ? 'वेदांग ज्योतिषम — व्यावसायिक ज्योतिष अभ्यास पीठ' : 'Vedang Jyotisham — Practice Management & Client CRM'}
                </h1>
                {isSuperAdminUser(currentUser) && (
                  <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-astro-secondary-surface text-astro-accent border border-astro-border">
                    {APP_VERSION} Management
                  </span>
                )}
              </div>
              <p className="text-xs text-astro-secondary-text">
                {isHi
                  ? 'क्लाइंट इतिहास, केस पाइपलाइन, आचार्य परामर्श प्रबंधन, डॉसियर रिपोर्टिंग एवं गोचर रडार'
                  : 'Longitudinal client records, case pipeline, Acharya consultation scheduling & verifiable dossier reports'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowSettingsModal(true)}
              className="p-2 rounded-lg bg-astro-bg hover:bg-astro-border/50 text-astro-text transition-colors cursor-pointer border border-astro-border"
              title={isHi ? 'सेटिंग्स' : 'Practice Settings'}
            >
              <Settings className="w-4 h-4 text-astro-secondary-text" />
            </button>
          </div>
        </div>

        {/* Active Client Chart Strip */}
        {kundali?.birthDetails && (
          <div className="px-4 md:px-6 py-2.5 bg-astro-bg border-b border-astro-border flex flex-wrap items-center justify-between gap-3 text-xs">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] uppercase font-bold text-astro-accent bg-[#FAF6ED] px-2 py-0.5 rounded border border-astro-border">
                {isHi ? 'सक्रिय सत्र कुंडली' : 'Active Client Chart'}
              </span>
              <strong className="text-astro-primary text-sm font-serif">{kundali.birthDetails.name}</strong>
              {onEditKundali && (
                <button
                  id="btn-management-edit-in-profile-name"
                  type="button"
                  onClick={onEditKundali}
                  className="p-1 rounded-md text-astro-accent hover:text-astro-primary hover:bg-[#FAF6ED] transition-colors cursor-pointer border border-astro-accent/30"
                  title={isHi ? 'जन्म विवरण (DOB, समय, स्थान) बदलें एवं पुनर्परिकलन करें' : 'Edit Birth Details (DOB, Time, Place) & Recalculate Chart'}
                  aria-label="Edit Birth Details"
                >
                  <Edit3 className="w-3.5 h-3.5 text-astro-accent" />
                </button>
              )}
              <span className="text-astro-secondary-text hidden sm:inline">•</span>
              <span className="text-astro-secondary-text hidden sm:inline">{formatDOB(kundali.birthDetails.date)} ({kundali.birthDetails.time}) • {kundali.birthDetails.placeName}</span>
            </div>
          </div>
        )}

        {/* Secondary Navigation Tabs */}
        <div className="flex items-center gap-1.5 px-3 py-2 overflow-x-auto bg-astro-bg border-t border-astro-border feature-nav-scroll">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeNavTab === item.id && !activeCaseWorkspace;
            return (
              <button
                key={item.id}
                onClick={() => {
                  setActiveNavTab(item.id);
                  setActiveCaseWorkspace(null);
                }}
                className={`px-3 py-2 rounded-xl text-xs font-bold flex items-center gap-2 transition-all cursor-pointer whitespace-nowrap ${
                  isActive
                    ? 'bg-astro-primary text-white shadow-xs'
                    : 'text-astro-secondary-text hover:bg-astro-card hover:text-astro-primary'
                }`}
              >
                <Icon className={`w-3.5 h-3.5 ${isActive ? 'text-astro-accent' : 'text-astro-secondary-text'}`} />
                <span>{isHi ? item.labelHi : item.labelEn}</span>
                {item.badge !== undefined && item.badge > 0 && (
                  <span
                    className={`text-[10px] px-1.5 py-0.2 rounded-full font-bold ${
                      isActive
                        ? 'bg-white text-astro-primary'
                        : 'bg-astro-border text-astro-text'
                    }`}
                  >
                    {item.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* 2. Active Screen Content Rendering */}
      {activeCaseWorkspace ? (
        <CaseWorkspace
          caseItem={activeCaseWorkspace}
          lang={lang}
          onBack={() => setActiveCaseWorkspace(null)}
          onUpdateCase={handleUpdateCase}
          onOpenProAnalysis={onOpenProAnalysis}
          onOpenAcharyaConsultation={onOpenAcharyaConsultation}
          onPrepareReport={(c) => {
            setActiveCaseWorkspace(null);
            setActiveNavTab('reports');
          }}
          onViewClient={(cId) => {
            setActiveCaseWorkspace(null);
            setActiveNavTab('clients');
          }}
        />
      ) : (
        <>
          {activeNavTab === 'overview' && (
            <PracticeOverview
              analytics={analytics}
              cases={cases}
              consultations={consultations}
              clients={clients}
              timingEvents={timingEvents}
              followUps={followUps}
              lang={lang}
              onNavigateTab={(tab) => setActiveNavTab(tab)}
              onOpenCaseWorkspace={(c) => setActiveCaseWorkspace(c)}
              onOpenProAnalysis={onOpenProAnalysis}
              onOpenAcharyaConsultation={onOpenAcharyaConsultation}
              onPrepareReport={() => setActiveNavTab('reports')}
              onOpenClientProfile={() => setActiveNavTab('clients')}
              onStartConsultation={(s) => {
                const matchedCase = cases.find((c) => c.id === s.caseId) || cases[0];
                if (onOpenAcharyaConsultation) {
                  onOpenAcharyaConsultation(matchedCase);
                }
              }}
              onNewClient={() => setShowNewClientModal(true)}
              onNewCase={() => setShowNewCaseModal(true)}
              onNewConsultation={() => setActiveNavTab('consultations')}
            />
          )}

          {activeNavTab === 'cases' && (
            <CasePipeline
              cases={cases}
              lang={lang}
              onOpenCaseWorkspace={(c) => setActiveCaseWorkspace(c)}
              onOpenProAnalysis={onOpenProAnalysis}
              onOpenAcharyaConsultation={onOpenAcharyaConsultation}
              onPrepareReport={() => setActiveNavTab('reports')}
              onNewCase={() => setShowNewCaseModal(true)}
              onMoveStage={handleMoveStage}
            />
          )}

          {activeNavTab === 'clients' && (
            <ClientManagementHub
              clients={clients}
              lang={lang}
              onOpenKundali={(client) => {
                if (onOpenKundaliForClient) {
                  onOpenKundaliForClient(client);
                }
              }}
              onNewClient={() => setShowNewClientModal(true)}
              onNewCaseForClient={(client) => {
                setShowNewCaseModal(true);
              }}
              onNewConsultationForClient={() => {
                setActiveNavTab('consultations');
              }}
              onGenerateReportForClient={() => {
                setActiveNavTab('reports');
              }}
              onOpenCaseWorkspace={(c) => setActiveCaseWorkspace(c)}
            />
          )}

          {activeNavTab === 'consultations' && (
            <ConsultationManager
              consultations={consultations}
              lang={lang}
              onStartConsultation={(s) => {
                const matchedCase = cases.find((c) => c.id === s.caseId) || cases[0];
                if (onOpenAcharyaConsultation) {
                  onOpenAcharyaConsultation(matchedCase);
                }
              }}
              onOpenReportForConsultation={() => {
                setActiveNavTab('reports');
              }}
              onNewConsultation={() => {
                alert('Session scheduling created in calendar.');
              }}
              onUpdateStatus={(id, st) => {
                updateConsultationStatus(id, st);
                refreshData();
              }}
            />
          )}

          {activeNavTab === 'reports' && (
            <ReportingCentre
              reports={reports}
              clients={clients}
              cases={cases}
              kundali={kundali}
              lang={lang}
              currentUser={currentUser}
              onSaveReport={handleSaveReport}
              onApproveReport={handleApproveReport}
              onDeliverReport={handleDeliverReport}
            />
          )}

          {activeNavTab === 'timing' && (
            <TimingRadar
              events={timingEvents}
              clients={clients}
              cases={cases}
              lang={lang}
              onOpenClientProfile={() => setActiveNavTab('clients')}
              onOpenCaseWorkspace={(c) => setActiveCaseWorkspace(c)}
              onOpenProAnalysis={onOpenProAnalysis}
            />
          )}

          {activeNavTab === 'followups' && (
            <FollowUpCentre
              followUps={followUps}
              clients={clients}
              cases={cases}
              lang={lang}
              onCompleteFollowUp={(id) => {
                completeFollowUpItem(id);
                refreshData();
              }}
              onOpenCaseWorkspace={(c) => setActiveCaseWorkspace(c)}
              onOpenClientProfile={() => setActiveNavTab('clients')}
            />
          )}

          {activeNavTab === 'analytics' && (
            <PracticeAnalytics analytics={analytics} lang={lang} />
          )}

          {activeNavTab === 'practice_crm' && (
            <ConsultantCRMView currentUser={currentUser} />
          )}
        </>
      )}

      {/* 3. Modal Dialogs */}
      {showSettingsModal && (
        <PracticeSettingsModal
          lang={lang}
          onClose={() => setShowSettingsModal(false)}
          onSaved={() => refreshData()}
        />
      )}

      {showNewClientModal && (
        <NewClientModal
          lang={lang}
          onClose={() => setShowNewClientModal(false)}
          onClientCreated={(cl) => {
            refreshData();
            setActiveNavTab('clients');
          }}
        />
      )}

      {showNewCaseModal && (
        <NewCaseModal
          clients={clients}
          lang={lang}
          onClose={() => setShowNewCaseModal(false)}
          onCaseCreated={(c) => {
            refreshData();
            setActiveCaseWorkspace(c);
          }}
        />
      )}
    </div>
  );
};
